/**
 * useChildCanvasPreview - Hook for loading child canvas previews
 *
 * Used by embedded canvas elements to load and cache child canvas definitions
 * for preview in the canvas builder.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import type { Canvas } from "../types";

const CANVAS_API_BASE = "/api/workspace/myvault/canvases";

/**
 * Helper to create headers with workspace ID
 */
const getWorkspaceHeaders = (workspaceId: string): Record<string, string> => ({
  "x-workspace-id": workspaceId,
});

export interface UseChildCanvasPreviewOptions {
  /** Whether to auto-fetch when canvasId changes */
  autoFetch?: boolean;
}

export interface UseChildCanvasPreviewResult {
  /** The loaded child canvas */
  canvas: Canvas | null;
  /** Whether the canvas is loading */
  loading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Manually trigger a fetch */
  fetch: () => Promise<void>;
  /** Clear the cached canvas */
  clear: () => void;
}

/**
 * Hook to fetch a child canvas for preview in embedded canvas elements.
 */
export function useChildCanvasPreview(
  workspaceId: string,
  canvasId: string | number | null | undefined,
  options: UseChildCanvasPreviewOptions = {},
): UseChildCanvasPreviewResult {
  const { autoFetch = true } = options;

  const [canvas, setCanvas] = useState<Canvas | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchCanvas = useCallback(async () => {
    if (!workspaceId || !canvasId) {
      setCanvas(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authFetchRef.current(
        `${CANVAS_API_BASE}/${canvasId}/full`,
        "fetch child canvas preview",
        { headers: getWorkspaceHeaders(workspaceId) },
      );

      setCanvas(response);
    } catch (err) {
      console.error("Child canvas preview fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch child canvas";
      setError(message);
      setCanvas(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, canvasId]);

  const clear = useCallback(() => {
    setCanvas(null);
    setError(null);
  }, []);

  // Auto-fetch when canvasId changes
  useEffect(() => {
    if (autoFetch && canvasId) {
      fetchCanvas();
    } else if (!canvasId) {
      clear();
    }
  }, [autoFetch, canvasId, fetchCanvas, clear]);

  return {
    canvas,
    loading,
    error,
    fetch: fetchCanvas,
    clear,
  };
}

/**
 * Cache for child canvas previews to avoid redundant fetches.
 */
interface ChildCanvasCache {
  [canvasId: string]: {
    canvas: Canvas;
    fetchedAt: number;
  };
}

const CACHE_TTL_MS = 60000; // 1 minute cache

/**
 * Hook to manage a cache of child canvas previews.
 * Useful when multiple embedded canvas elements reference the same child canvas.
 */
export function useChildCanvasCache(workspaceId: string) {
  const [cache, setCache] = useState<ChildCanvasCache>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const getCanvas = useCallback(
    async (canvasId: string | number): Promise<Canvas | null> => {
      const key = String(canvasId);

      // Check cache
      const cached = cache[key];
      if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
        return cached.canvas;
      }

      // Check if already loading
      if (loading[key]) {
        return null;
      }

      try {
        setLoading((prev) => ({ ...prev, [key]: true }));
        setErrors((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });

        const response = await authFetchRef.current(
          `${CANVAS_API_BASE}/${canvasId}/full`,
          "fetch child canvas for cache",
          { headers: getWorkspaceHeaders(workspaceId) },
        );

        setCache((prev) => ({
          ...prev,
          [key]: {
            canvas: response,
            fetchedAt: Date.now(),
          },
        }));

        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to fetch canvas";
        setErrors((prev) => ({ ...prev, [key]: message }));
        return null;
      } finally {
        setLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [cache, loading, workspaceId],
  );

  const getCachedCanvas = useCallback(
    (canvasId: string | number): Canvas | null => {
      const key = String(canvasId);
      const cached = cache[key];
      if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
        return cached.canvas;
      }
      return null;
    },
    [cache],
  );

  const isLoading = useCallback(
    (canvasId: string | number): boolean => {
      return !!loading[String(canvasId)];
    },
    [loading],
  );

  const getError = useCallback(
    (canvasId: string | number): string | null => {
      return errors[String(canvasId)] || null;
    },
    [errors],
  );

  const invalidate = useCallback((canvasId: string | number) => {
    const key = String(canvasId);
    setCache((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const invalidateAll = useCallback(() => {
    setCache({});
  }, []);

  return {
    getCanvas,
    getCachedCanvas,
    isLoading,
    getError,
    invalidate,
    invalidateAll,
  };
}

export default useChildCanvasPreview;
